<template>
  <div
    class="lightbox"
    :class="{
      landscape,
      active,
      'portrait': !landscape,
      'next-slide': isNext,
      'previous-slide': isPrev,
      'project-page': $route.name === 'project'
    }"
    :style="{ 'background-color': backgroundColor }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mousemove="onMouseMove"
    @click="onClick"
    >
    <div class="lightbox-project">
      <div ref="projects" class="lightbox-project-inner" :style="{ 'background-color': backgroundColor }" :key="uid">
        <project v-if="$route.name === 'project' || !phone" :uid="uid" :landscape="landscape" @file-changed="onFileChanged"></project>
        <template v-else>
          <project
            v-for="project in projects"
            :uid="project.uid"
            :key="project.uid"
            :landscape="landscape"
            @file-changed="onFileChanged"
            @next-project="onNextProject"
            @previous-project="onPreviousProject"
            ></project>
        </template>
      </div>
    </div>
    <div class="lightbox-caption" v-if="$route.name === 'project' || !phone" v-html="currentCaption"></div>
  </div>
</template>

<script>
import EventBus from '../util/EventBus'
import Project from './Project.vue'
import { tns } from 'tiny-slider/src/tiny-slider.module'

export default {
  name: 'lightbox',
  props: {
    uid: {
      default: null
    },
    seq: {
      default: 0
    },
    projects: {
      default () {
        return []
      }
    }
  },
  components: {
    'project': Project
  },
  data () {
    return {
      active: false,
      currentCaption: '',
      slider: null,
      isNext: false,
      isPrev: false
    }
  },
  computed: {
    projectsPage () {
      return this.$store.getters.getPagesByType('Projects')[0]
    },
    page () {
      return this.$store.getters.getPageByUID(this.uid)
    },
    phone () {
      // NB: no captions on mobile
      return this.$store.state.app.phone
    },
    landscape () {
      // NB: we disregard landscape if we're viewing the project page on mobile
      if (this.$route.name === 'project' && this.phone) {
        return false
      }

      return this.page.content.landscape && (this.page.content.landscape === '1' || this.page.content.landscape === 'true')
    },
    backgroundColor () {
      if (this.landscape) {
        return this.projectsPage.content[`lightbox_landscape_background_color`]
      }

      return this.projectsPage.content[`lightbox_${this.seq + 1}_background_color`]
    }
  },
  created () {
    this.currentCaption = this.page.text
  },
  mounted () {
    if (this.phone) {
      this.setupSlider()
    }
  },
  watch: {
    uid (newValue, oldValue) {
      this.currentCaption = this.page.text
      this.active = false
    },
    phone (newValue, oldValue) {
      if (newValue) {
        this.setupSlider()
      } else {
        this.teardownSlider()
      }
    }
  },
  methods: {
    onNextProject () {
      this.slider.goTo('next')
    },
    onPreviousProject () {
      this.slider.goTo('prev')
    },
    setupSlider () {
      let sliderOptions = {
        container: this.$refs.projects,
        items: 1,
        loop: true,
        nav: false,
        controls: false
      }

      this.slider = tns(sliderOptions)
      if (!this.slider) {
        console.warn('No slider initialized', this.slider)
        return
      }

      // react to changes in the index to notify parents of project
      // change for phone preloading
      this.slider.events.on('indexChanged', this.onIndexChanged)

      // preload first project files when first loading on phone
      if (this.phone) {
        const info = this.slider.getInfo()
        this.onIndexChanged(info, 'indexChanged')
      }
    },
    onIndexChanged (info, eventName) {
      this.$emit('project-changed', { info, eventName, project: this.projects[info.index - 1] })
    },
    teardownSlider () {
      if (this.slider) {
        this.slider.destroy()
      }
    },
    onMouseEnter (event) {
      this.active = true
      this.$emit('mouseenter', event)
      EventBus.$emit('lightbox-mouseenter', event)
    },
    onMouseLeave (event) {
      this.active = false
      this.isNext = false
      this.isPrev = false
      this.$emit('mouseleave', event)
      EventBus.$emit('lightbox-mouseleave', event)
    },
    onClick (event) {
      this.$emit('click', event)
      EventBus.$emit('lightbox-click', event)
    },
    onFileChanged (file) {
      // set to current file caption if there is one.
      if (this.landscape && file.caption && file.caption.trim()) {
        this.currentCaption = file.caption
      }
    },
    onMouseMove (event) {
      const rect = this.$el.getBoundingClientRect()
      if (event.pageX > rect.left && event.pageX <= rect.left + (this.$el.clientWidth / 2)) {
        this.isPrev = false
        this.isNext = true
        return
      }

      if (event.pageX > rect.left + (this.$el.clientWidth / 2) && event.pageX < rect.right) {
        this.isNext = false
        this.isPrev = true
        return
      }

      this.isNext = false
      this.isPrev = false
    }
  }
}
</script>

<style lang="scss">
@import '../assets/styles/base';

.lightbox {
  background: black;
  height: 0;
  padding-bottom: 44.05%;
  float: left;
  width: calc(33.3333% - #{$body-padding});
  margin-left: $body-padding;
  position: relative;

  &.next-slide {
    cursor: w-resize;
  }

  &.previous-slide {
    cursor: e-resize;
  }

  @media (max-width: $phone) {
    width: calc(100% - #{$body-padding});
    padding-bottom: 133%;

    &.project-page {
      width: 100%;
      margin-left: 0;
    }
  }

  &.hidden {
    opacity: 0;
  }

  .lightbox-project {
    height: calc(100% - #{$body-padding * 2});
    width: calc(100% - #{$body-padding * 2});
    overflow: hidden;
    position: absolute;
    top: $body-padding;
    left: $body-padding;
  }

  .lightbox-project-inner {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  .lightbox-caption {
    padding-top: 1rem;
    position: absolute;
    width: 100%;
    top: 100%;
    display: none;
  }

  &.landscape {
    width: calc(66.6666% - #{$body-padding});

    @media (max-width: $phone) {
      display: none;
    }

    .lightbox-project {
      height: 100%;
      width: 100%;
      overflow: hidden;
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  // NB: always show the caption on mobile so we have some information
  &.project-page .lightbox-caption,
  &.active .lightbox-caption {
    display: block;
  }
}

.no-landscape .lightbox {
  @media (max-width: $phone) {
    &:first-child {
      display: none;
    }

    &:nth-child(2) {
      display: none;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.slide-down-enter {
  -webkit-transform: translate3d(0, -100%, 0);
  transform: translate3d(0, -100%, 0);
}

.slide-down-leave-to {
  -webkit-transform: translate3d(0, 100%, 0);
  transform: translate3d(0, 100%, 0);
}
</style>



// WEBPACK FOOTER //
// client/components/Lightbox.vue